import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { WRAP } from '../styles/layout';

const BG   = '#09060F';
const C    = '#00D4AA';
const R    = '0,212,170';
const CP   = '#7C3AED';
const RP   = '124,58,237';
const LAV  = '#EDE9F8';
const LAV2 = '#C8C0EC';
const LAV3 = '#9B90BA';

const POST_URL   = 'https://revupai.com/blog/secure-developer-ai';
const POST_TITLE = 'How to Secure Developer AI Tools Like Claude Code, Cursor, and Copilot';

/* ── FAQ — single source of truth for both on-page render and JSON-LD ── */
const FAQS = [
  {
    q: 'Can you secure AI coding assistants like Claude Code, Cursor, and GitHub Copilot?',
    a: 'Yes. RevSec secures the developer AI surface by discovering which agents are running, governing what they can read, run, and ship, routing risky actions for fast human approval, and keeping a full audit trail — without removing the tools or slowing developers down.',
  },
  {
    q: 'Does securing developer AI slow developers down?',
    a: 'It shouldn’t, and that’s the point. Effective governance lets routine, low-risk work proceed at full speed and only pauses the genuinely risky actions for a fast human check. Controls that add broad friction get bypassed; controls that target real risk get adopted.',
  },
  {
    q: 'Should we ban AI coding tools?',
    a: 'No. Banning drives developers to personal accounts and unmanaged devices, which removes your visibility into the risk without removing the risk. The effective path is to allow the tools and govern what they can do.',
  },
  {
    q: 'What is the biggest risk with AI coding agents?',
    a: 'Their reach combined with a lack of oversight. They can access secrets, source code, internal systems, and production — and in most organizations there’s no visibility into what they touch, no control over risky actions, and no audit trail.',
  },
  {
    q: 'How do you govern what an AI agent can access?',
    a: 'By gaining visibility into every agent, defining and enforcing policy on what each can reach and do, protecting sensitive data before it leaves, requiring human approval for consequential actions, and logging all of it for audit and compliance.',
  },
  {
    q: 'What is Operated AI?',
    a: 'Operated AI is the practice of continuously running AI governance as a service — discovering, monitoring, and enforcing in real time — rather than installing security tooling once and leaving it static. It treats AI governance like a live security function, not a one-time deployment.',
  },
  {
    q: 'Can RevSec help prove our AI is governed for compliance?',
    a: 'Yes. Because RevSec records what AI agents do and how policy is enforced, it produces the evidence needed to demonstrate AI governance to customers, auditors, and regulators.',
  },
  {
    q: 'Is this about "vibe coding" security?',
    a: 'Yes. "Vibe coding" — building software by prompting AI coding agents like Claude Code, Cursor, and GitHub Copilot in natural language — is exactly the surface this covers. The more your team vibe-codes, the more an agent reads your codebase, opens secrets, runs commands, and ships code on its own. RevSec makes vibe coding safe to do at scale: it discovers those agents, governs what they can read, run, and ship, routes risky actions to a human, and keeps a full audit trail — without slowing the workflow down.',
  },
];

/* ── Structured data: Article + FAQPage JSON-LD (injected into <head>) ── */
function StructuredData() {
  useEffect(() => {
    const article = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: POST_TITLE,
      description: 'The real risks of developer AI coding agents, why banning them backfires, and how to govern the developer AI surface without slowing engineering down.',
      author: { '@type': 'Organization', name: 'RevUp AI' },
      publisher: {
        '@type': 'Organization',
        name: 'RevUp AI',
        logo: { '@type': 'ImageObject', url: 'https://revupai.com/logo.png' },
      },
      datePublished: '2026-06-25',
      dateModified: '2026-06-25',
      mainEntityOfPage: POST_URL,
      image: 'https://revupai.com/og-image.png',
    };

    const faqPage = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQS.map(({ q, a }) => ({
        '@type': 'Question',
        name: q,
        acceptedAnswer: { '@type': 'Answer', text: a },
      })),
    };

    const nodes = [article, faqPage].map((schema, i) => {
      const id = `blog-secure-dev-ai-jsonld-${i}`;
      const existing = document.getElementById(id);
      if (existing) existing.remove();
      const script = document.createElement('script');
      script.id = id;
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
      return id;
    });

    return () => nodes.forEach(id => { const el = document.getElementById(id); if (el) el.remove(); });
  }, []);

  return null;
}

/* ── Reading progress bar ── */
function ReadingProgress() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const fn = () => {
      const el  = document.documentElement;
      const pct = (el.scrollTop / (el.scrollHeight - el.clientHeight)) * 100;
      setProgress(Math.min(100, pct));
    };
    window.addEventListener('scroll', fn);
    return () => window.removeEventListener('scroll', fn);
  }, []);
  return (
    <div style={{ position:'fixed', top:64, left:0, right:0, height:2, background:'rgba(255,255,255,.06)', zIndex:100 }}>
      <div style={{ height:'100%', width:`${progress}%`, background:`linear-gradient(90deg,${CP},${C})`, transition:'width .1s linear' }}/>
    </div>
  );
}

/* ── Share buttons — floating sidebar on desktop, inline on mobile ── */
function ShareButtons({ mobile=false }) {
  const [copied, setCopied] = useState(false);

  const copyLink = () => {
    navigator.clipboard.writeText(POST_URL).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const shareLinkedIn = () => window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(POST_URL)}`, '_blank');
  const shareX        = () => window.open(`https://x.com/intent/tweet?url=${encodeURIComponent(POST_URL)}&text=${encodeURIComponent(POST_TITLE)}`, '_blank');

  if (mobile) {
    return (
      <div style={{ display:'flex', alignItems:'center', gap:8, margin:'32px 0', padding:'16px 20px', background:'rgba(255,255,255,.03)', border:'1px solid rgba(255,255,255,.08)', borderRadius:10 }}>
        <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:12, fontWeight:600, color:LAV3, marginRight:4 }}>Share:</span>
        <ShareBtn onClick={shareLinkedIn} label="LinkedIn" color="#0A66C2" icon={
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></svg>
        }/>
        <ShareBtn onClick={shareX} label="X" color="#ffffff" icon={
          <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
        }/>
        <ShareBtn onClick={copyLink} label={copied ? 'Copied!' : 'Copy link'} color={copied ? C : LAV3} icon={
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
        }/>
      </div>
    );
  }

  return (
    <div style={{ position:'sticky', top:100, display:'flex', flexDirection:'column', gap:8, alignItems:'center' }}>
      <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:9, fontWeight:700, letterSpacing:2, textTransform:'uppercase', color:LAV3, marginBottom:4 }}>Share</span>
      <IconBtn onClick={shareLinkedIn} title="Share on LinkedIn" color="#0A66C2" bg="rgba(10,102,194,.1)" border="rgba(10,102,194,.25)" icon={
        <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></svg>
      }/>
      <IconBtn onClick={shareX} title="Share on X" color={LAV2} bg="rgba(255,255,255,.06)" border="rgba(255,255,255,.12)" icon={
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
      }/>
      <IconBtn onClick={copyLink} title={copied ? 'Copied!' : 'Copy link'} color={copied ? C : LAV3} bg={copied ? `rgba(${R},.1)` : 'rgba(255,255,255,.04)'} border={copied ? `rgba(${R},.3)` : 'rgba(255,255,255,.1)'} icon={
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
      }/>
    </div>
  );
}

function IconBtn({ onClick, title, color, bg, border, icon }) {
  const [hov, setHov] = useState(false);
  return (
    <button onClick={onClick} title={title}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ width:36, height:36, borderRadius:9, background:hov?`rgba(255,255,255,.1)`:bg, border:`1px solid ${hov?'rgba(255,255,255,.2)':border}`, color, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', transition:'all .2s ease', padding:0 }}
    >{icon}</button>
  );
}

function ShareBtn({ onClick, label, color, icon }) {
  return (
    <button onClick={onClick}
      style={{ display:'flex', alignItems:'center', gap:5, padding:'6px 12px', borderRadius:7, background:'rgba(255,255,255,.05)', border:'1px solid rgba(255,255,255,.1)', color, cursor:'pointer', fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:12, fontWeight:500, transition:'all .2s ease' }}
      onMouseEnter={e => { e.currentTarget.style.background='rgba(255,255,255,.1)'; }}
      onMouseLeave={e => { e.currentTarget.style.background='rgba(255,255,255,.05)'; }}
    >{icon}{label}</button>
  );
}

/* ── Typography helpers ── */
const H2 = ({ children }) => (
  <h2 style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:800, fontSize:'clamp(22px,2vw,30px)', color:LAV, margin:'56px 0 16px', letterSpacing:'-0.5px', lineHeight:1.2 }}>{children}</h2>
);
const P = ({ children }) => (
  <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:16, fontWeight:300, lineHeight:1.9, color:LAV2, margin:'0 0 20px' }}>{children}</p>
);
const Strong = ({ children, color=LAV }) => (
  <strong style={{ fontWeight:600, color }}>{children}</strong>
);
const Li = ({ children }) => (
  <li style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:15.5, fontWeight:300, lineHeight:1.85, color:LAV2, margin:'0 0 10px', paddingLeft:8 }}>{children}</li>
);
const Callout = ({ color='#10B981', rgb='16,185,129', icon, title, children }) => (
  <div style={{ margin:'32px 0', padding:'24px 28px', background:`rgba(${rgb},.05)`, border:`1px solid rgba(${rgb},.2)`, borderLeft:`3px solid ${color}`, borderRadius:'0 10px 10px 0' }}>
    {icon && <div style={{ fontSize:18, marginBottom:8 }}>{icon}</div>}
    {title && <div style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:11, fontWeight:700, letterSpacing:2, textTransform:'uppercase', color, marginBottom:10 }}>{title}</div>}
    <div style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:15, fontWeight:300, lineHeight:1.8, color:LAV2 }}>{children}</div>
  </div>
);
const FeatureChip = ({ name, color=C, rgb=R }) => (
  <span style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'2px 9px', borderRadius:100, background:`rgba(${rgb},.1)`, border:`1px solid rgba(${rgb},.25)`, fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:11, fontWeight:700, color, letterSpacing:.4, margin:'0 3px 3px 0', verticalAlign:'middle', whiteSpace:'nowrap' }}>
    <span style={{ width:4, height:4, borderRadius:'50%', background:color, display:'inline-block', flexShrink:0 }}/>
    {name}
  </span>
);
const Code = ({ children }) => (
  <code style={{ background:'rgba(255,255,255,.07)', padding:'1px 6px', borderRadius:4, fontSize:13, fontFamily:"'DM Mono',monospace,system-ui" }}>{children}</code>
);

export default function BlogSecureDeveloperAI() {
  const navigate = useNavigate();

  return (
    <>
      <Helmet>
        <title>How to Secure Developer AI Tools (Claude Code, Cursor, Copilot) | RevUp AI</title>
        <meta name="description" content="Developers already use AI coding agents that can reach your secrets and ship code. Learn the real risks and how to govern developer AI without slowing teams down." />
        <meta name="keywords" content="secure developer AI, vibe coding security, vibe code security, securing vibe coding, Claude Code security, Cursor security, GitHub Copilot governance, AI coding agent risk, AI coding agent governance, AI agent governance, developer AI surface, AI CISO, prompt injection, AI audit trail, Operated AI" />
        <link rel="canonical" href={POST_URL} />
        <meta property="og:title" content={POST_TITLE} />
        <meta property="og:description" content="The real risks of developer AI coding agents, why banning them backfires, and how to govern the developer AI surface without slowing engineering down." />
        <meta property="og:url" content={POST_URL} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content="https://revupai.com/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="article:published_time" content="2026-06-25" />
        <meta name="article:author" content="RevUp AI" />
      </Helmet>

      <StructuredData/>
      <ReadingProgress/>
      <style>{`
        * { box-sizing: border-box; }
        @media(max-width:900px){.share-sidebar{display:none!important;}}
        @media(min-width:901px){.share-mobile{display:none!important;}}
        @media(max-width:767px){
          .paths-grid { grid-template-columns: 1fr !important; }
          .body-grid  { grid-template-columns: 1fr !important; }
          .things-grid{ grid-template-columns: 1fr !important; }
        }
      `}</style>

      <div style={{ background:BG, minHeight:'100vh', paddingTop:80 }}>

        {/* ── Header ── */}
        <div style={{ borderBottom:`1px solid rgba(255,255,255,.06)`, padding:'clamp(48px,6vh,72px) 0 clamp(40px,5vh,56px)' }}>
          <div style={{ ...WRAP, maxWidth:1000 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:24 }}>
              <button onClick={() => navigate('/blog')} style={{ background:'none', border:'none', cursor:'pointer', fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13, color:LAV3, padding:0, transition:'color .2s' }}
                onMouseEnter={e => e.currentTarget.style.color=C}
                onMouseLeave={e => e.currentTarget.style.color=LAV3}
              >Blog</button>
              <span style={{ color:'rgba(255,255,255,.2)', fontSize:13 }}>›</span>
              <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13, color:LAV3 }}>Developer Security</span>
            </div>
            <div style={{ display:'inline-flex', alignItems:'center', gap:6, padding:'4px 12px', borderRadius:100, background:`rgba(${R},.1)`, border:`1px solid rgba(${R},.25)`, marginBottom:20 }}>
              <span style={{ fontSize:11, fontWeight:700, letterSpacing:1.5, textTransform:'uppercase', color:C, fontFamily:"'DM Sans',system-ui,sans-serif" }}>Developer Security</span>
            </div>
            <h1 style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:'clamp(30px,3.5vw,50px)', color:LAV, margin:'0 0 20px', letterSpacing:'-1.5px', lineHeight:1.1 }}>
              How to Secure Developer AI Tools Like Claude Code, Cursor, and Copilot
            </h1>
            <div style={{ display:'flex', alignItems:'center', gap:16, marginBottom:28, flexWrap:'wrap' }}>
              <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                <div style={{ width:28, height:28, borderRadius:'50%', background:`linear-gradient(135deg,${CP},${C})`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700, color:'white', fontFamily:"'DM Sans',system-ui,sans-serif" }}>R</div>
                <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13, color:LAV2, fontWeight:500 }}>RevUp AI</span>
              </div>
              <span style={{ width:3, height:3, borderRadius:'50%', background:'rgba(255,255,255,.2)' }}/>
              <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13, color:LAV3 }}>Last updated June 2026</span>
              <span style={{ width:3, height:3, borderRadius:'50%', background:'rgba(255,255,255,.2)' }}/>
              <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13, color:LAV3 }}>6 min read</span>
            </div>
            <div style={{ padding:'24px 28px', background:'rgba(124,58,237,.07)', border:'1px solid rgba(124,58,237,.2)', borderRadius:12 }}>
              <div style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:11, fontWeight:700, letterSpacing:2, textTransform:'uppercase', color:'rgba(167,139,250,.8)', marginBottom:10 }}>In short</div>
              <p style={{ fontFamily:"'Fraunces',Georgia,serif", fontStyle:'italic', fontSize:'clamp(16px,1.4vw,19px)', fontWeight:400, color:LAV2, margin:0, lineHeight:1.6 }}>
                Your developers are already using AI coding agents, and those agents can read your entire codebase, access secrets, run commands, and ship code. The answer isn't to ban them — it's to govern what they can do.
              </p>
            </div>
          </div>
        </div>

        {/* ── Body with sidebar share ── */}
        <div style={{ ...WRAP, maxWidth:1000, padding:'clamp(48px,6vh,72px) clamp(20px,4vw,40px)' }}>
          <div className="body-grid" style={{ display:'grid', gridTemplateColumns:'1fr 48px', gap:40, alignItems:'flex-start' }}>

            {/* Article content */}
            <div style={{ minWidth:0 }}>

              {/* Mobile share */}
              <div className="share-mobile">
                <ShareButtons mobile={true}/>
              </div>

              <P>This guide explains the real risks of developer AI coding agents — the engine behind "vibe coding," where teams build software by prompting tools like Claude Code, Cursor, and Copilot — what companies are getting right and wrong, and what to consider when securing the developer AI surface without slowing engineering down.</P>

              <H2>What is the risk with developer AI tools?</H2>
              <P>The risk is reach, not malice. AI coding agents like Claude Code, Cursor, and GitHub Copilot can read your entire codebase, open files containing production secrets, run commands, call internal systems, and ship code — often in seconds, and usually with no human review and no record. The danger isn't that the tools are malicious. It's that they have broad access to your most sensitive assets, and most organisations have no visibility into what they're doing.</P>
              <P>Consider a routine example. A developer types <Strong color={LAV2}>"refactor the billing module and ship it to staging."</Strong> In the next few seconds the agent reads thousands of files across multiple repositories, opens the environment configuration including production secrets, edits dozens of files, deploys to staging, and opens a pull request that merges itself. Nobody reviewed it. Nobody approved it. And in most companies, nobody in security knows it happened.</P>

              <H2>Why do AI coding agents create more risk than AI chatbots?</H2>
              <P>Because coding agents have far more access — yet usually get far less governance. This is the central blind spot in most AI security programs: <Strong>the least powerful AI gets the most oversight, and the most powerful gets almost none.</Strong></P>
              <P>When an employee pastes text into a chatbot, most companies have policies, data-loss controls, and browser monitoring in place. But the AI agent inside the codebase — the one that can read every secret, run commands, and ship to production — typically runs on a vendor default and a developer's good intentions. The tool with the deepest reach into your crown jewels is the one nobody is watching.</P>

              <H2>What can go wrong when AI agents are ungoverned?</H2>
              <P>Four things happen in real engineering organisations, and none of them require a bad actor:</P>

              {[
                { title:'Sensitive data leaves quietly', color:'#10B981', rgb:'16,185,129', desc:'An agent reads a config file during a routine task, and a secret, customer record, or proprietary code ends up in a prompt, a log, or a third-party model’s context.' },
                { title:'Risky actions happen with no human in the loop', color:'#F59E0B', rgb:'245,158,11', desc:'Code ships, infrastructure changes, data gets modified — actions that would require sign-off from a person, but happen silently when an agent does them.' },
                { title:'The blast radius is invisible', color:'#EF4444', rgb:'239,68,68', desc:'When something goes wrong, the first question — what could this agent actually reach? — has no answer, because no one mapped it.' },
                { title:'There is no audit trail', color:'#8B5CF6', rgb:'139,92,246', desc:'When a customer, board, or regulator asks how AI in your development pipeline is governed, most teams have nothing to show.' },
              ].map((s, i) => (
                <div key={i} style={{ display:'flex', gap:18, marginBottom:16, padding:'20px 24px', background:'rgba(255,255,255,.02)', border:`1px solid rgba(${s.rgb},.15)`, borderRadius:12 }}>
                  <div style={{ flexShrink:0, width:32, height:32, borderRadius:9, background:`rgba(${s.rgb},.1)`, border:`1px solid rgba(${s.rgb},.25)`, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:"'Fraunces',Georgia,serif", fontSize:14, fontWeight:900, color:s.color }}>{i+1}</div>
                  <div>
                    <div style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:15, fontWeight:600, color:LAV, marginBottom:6, lineHeight:1.3 }}>{s.title}</div>
                    <div style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:14, fontWeight:300, color:LAV2, lineHeight:1.75 }}>{s.desc}</div>
                  </div>
                </div>
              ))}

              <Callout color="#00D4AA" rgb="0,212,170" icon="📊" title="From the field">
                In RevUp AI's developer-surface risk reviews, a majority of environments we assess have at least one AI coding agent with access to production secrets that no one had formally reviewed or approved.
              </Callout>

              <H2>Should companies ban AI coding tools?</H2>
              <P>No. Banning AI coding tools is the worst of the available options, because it removes your visibility into the risk without removing the risk itself. Organisations take one of three paths, and only one works:</P>

              <div className="paths-grid" style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16, margin:'28px 0 32px' }}>
                {[
                  { n:'01', label:'Ban the tools', color:'#EF4444', rgb:'239,68,68', verdict:"Doesn't work", desc:"Developers don't stop using AI — they move to personal accounts and unmanaged devices, out of sight. You lose visibility, and you signal to your best engineers that they can't use the best tools." },
                  { n:'02', label:'Allow everything and hope', color:'#F59E0B', rgb:'245,158,11', verdict:'Default, not a decision', desc:'Great developer experience, but no idea what agents can reach, no way to stop a leak, and nothing to show an auditor. Most companies are here by default, not by decision.' },
                  { n:'03', label:'Allow the tools, govern what they do', color:'#00D4AA', rgb:'0,212,170', verdict:'The only path that works', desc:'You get the productivity and the control. This is the path forward-thinking security and platform teams are moving to.' },
                ].map((p, i) => (
                  <div key={i} style={{ padding:'22px 22px 24px', background:`rgba(${p.rgb},.04)`, border:`1px solid rgba(${p.rgb},.2)`, borderRadius:12, display:'flex', flexDirection:'column' }}>
                    <div style={{ fontFamily:"'Fraunces',Georgia,serif", fontSize:13, fontWeight:900, color:p.color, marginBottom:12 }}>{p.n}</div>
                    <div style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:15.5, fontWeight:700, color:LAV, marginBottom:8, lineHeight:1.3 }}>{p.label}</div>
                    <div style={{ display:'inline-flex', alignSelf:'flex-start', padding:'3px 10px', borderRadius:100, background:`rgba(${p.rgb},.12)`, border:`1px solid rgba(${p.rgb},.3)`, marginBottom:14 }}>
                      <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:10, fontWeight:700, letterSpacing:.6, textTransform:'uppercase', color:p.color }}>{p.verdict}</span>
                    </div>
                    <div style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:13.5, fontWeight:300, color:LAV2, lineHeight:1.7 }}>{p.desc}</div>
                  </div>
                ))}
              </div>

              <H2>What should you consider when securing developer AI?</H2>
              <P>Focus on outcomes, not on which model or tool is "best." Six questions matter more than the rest:</P>

              {[
                { tag:'Visibility', desc:'Do you know which AI tools and agents are running across engineering — including the ones nobody told you about?' },
                { tag:'Access', desc:'For each one, do you know what it can actually reach — which repositories, secrets, systems, and data?' },
                { tag:'Data protection', desc:'What stops sensitive data from leaving through an AI agent?' },
                { tag:'Control over risky actions', desc:'When an agent attempts something irreversible, can a human approve it quickly — without stalling the developer?' },
                { tag:'Evidence', desc:'Could you prove your AI is governed to a customer or regulator?' },
                { tag:'Developer experience', desc:'Does your approach keep developers fast, or quietly push them to route around you?' },
              ].map((q, i) => (
                <div key={i} style={{ display:'flex', gap:16, marginBottom:14, alignItems:'flex-start' }}>
                  <div style={{ flexShrink:0, marginTop:2, padding:'4px 11px', borderRadius:7, background:`rgba(${R},.08)`, border:`1px solid rgba(${R},.22)`, fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:11.5, fontWeight:700, color:C, letterSpacing:.3, whiteSpace:'nowrap' }}>{q.tag}</div>
                  <div style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:15, fontWeight:300, color:LAV2, lineHeight:1.7 }}>{q.desc}</div>
                </div>
              ))}

              <P>That last point is where most security tools fail. Controls that slow developers down get bypassed, not adopted. Any approach that treats the developer as the adversary is doomed, because the developer always wins.</P>

              <Callout color="#7C3AED" rgb="124,58,237" icon="💡" title="The key insight">
                <Strong color={LAV}>The goal is not to gate your engineers — it's to make the tools they already use safe to use.</Strong>
              </Callout>

              <H2>How does RevSec secure developer AI?</H2>
              <P>RevSec secures the AI surface your developers already use, without taking their tools away or slowing them down. Its guiding principle is simple: <Strong>don't block AI, govern it.</Strong> Functionally, RevSec does four things:</P>

              <div className="things-grid" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, margin:'28px 0 8px' }}>
                {[
                  { chip:'Discover', desc:'Finds the AI agents and tools running across your organisation, so the ones nobody told you about stop being invisible.' },
                  { chip:'Govern', desc:'Governs what agents can read, run, and ship — sensitive data is protected before it can leave, and policy is enforced on what agents are allowed to do.' },
                  { chip:'Route to a human', desc:'Routes risky actions to a human for a fast approval instead of letting them happen silently — so developers keep moving and security keeps control.' },
                  { chip:'Record everything', desc:'Records everything for audit, so when someone asks how your AI is governed, you have a complete, defensible answer.' },
                ].map((t, i) => (
                  <div key={i} style={{ padding:'22px 24px', background:`rgba(${RP},.05)`, border:`1px solid rgba(${RP},.2)`, borderRadius:12 }}>
                    <div style={{ marginBottom:12 }}><FeatureChip name={t.chip} color="#A78BFA" rgb={RP}/></div>
                    <div style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:14, fontWeight:300, color:LAV2, lineHeight:1.75 }}>{t.desc}</div>
                  </div>
                ))}
              </div>

              <Callout color="#7C3AED" rgb="124,58,237" icon="🛡" title="Operated AI">
                Crucially, RevSec is <Strong color={LAV}>operated, not just deployed.</Strong> Governance isn't something you install once and forget — RevSec runs continuously, discovering, monitoring, and enforcing the way a real security function does. We call this approach <Strong color="#A78BFA">Operated AI</Strong>: security for the agent era that is always on, so your team doesn't have to be.
              </Callout>

              <P>The result is the outcome that used to feel impossible: your developers keep their speed, and your organisation keeps control. You get to say yes to AI.</P>

              <H2>Frequently asked questions</H2>
              {FAQS.map((f, i) => (
                <div key={i} style={{ padding:'22px 24px', marginBottom:14, background:'rgba(255,255,255,.02)', border:'1px solid rgba(255,255,255,.08)', borderRadius:12 }}>
                  <div style={{ display:'flex', gap:12, alignItems:'flex-start', marginBottom:10 }}>
                    <span style={{ flexShrink:0, color:C, fontFamily:"'Fraunces',Georgia,serif", fontSize:18, fontWeight:900, lineHeight:1.1 }}>Q</span>
                    <h3 style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:700, fontSize:'clamp(16px,1.4vw,19px)', color:LAV, margin:0, letterSpacing:'-0.2px', lineHeight:1.35 }}>{f.q}</h3>
                  </div>
                  <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:15, fontWeight:300, color:LAV2, lineHeight:1.8, margin:0, paddingLeft:30 }}>{f.a}</p>
                </div>
              ))}

              {/* Bottom share buttons — mobile */}
              <div className="share-mobile">
                <ShareButtons mobile={true}/>
              </div>

              {/* CTA */}
              <div style={{ marginTop:64, padding:'36px 40px', background:`rgba(${RP},.06)`, border:`1px solid rgba(${RP},.2)`, borderRadius:16 }}>
                <div style={{ fontSize:11, fontWeight:700, letterSpacing:2.5, textTransform:'uppercase', color:`rgba(167,139,250,.7)`, marginBottom:12, fontFamily:"'DM Sans',system-ui,sans-serif" }}>RevSec — AI CISO by RevUp AI</div>
                <h3 style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:800, fontSize:'clamp(20px,1.8vw,26px)', color:LAV, margin:'0 0 12px', letterSpacing:'-0.5px' }}>Say yes to AI — with the controls to back it.</h3>
                <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:15, fontWeight:300, color:LAV2, margin:'0 0 8px', lineHeight:1.7 }}>
                  Securing the AI your developers already use starts with knowing what's running and what it can reach. RevSec discovers every agent, governs what they can read, run, and ship, routes risky actions for fast human approval, and records everything for audit.
                </p>
                <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:15, fontWeight:300, color:LAV2, margin:'0 0 28px', lineHeight:1.7 }}>
                  Your developers keep their tools and their speed. Your organisation keeps the control.
                </p>
                <div style={{ display:'flex', gap:12, flexWrap:'wrap' }}>
                  <button onClick={() => navigate('/contact')}
                    style={{ padding:'13px 28px', borderRadius:9, background:CP, color:'white', border:'none', cursor:'pointer', fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:14, fontWeight:700, boxShadow:`0 8px 24px rgba(${RP},.35)`, transition:'all .22s ease' }}
                    onMouseEnter={e => { e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow=`0 12px 32px rgba(${RP},.5)`; }}
                    onMouseLeave={e => { e.currentTarget.style.transform='none'; e.currentTarget.style.boxShadow=`0 8px 24px rgba(${RP},.35)`; }}
                  >Get a Developer-Surface Risk Review →</button>
                  <button onClick={() => navigate('/ai-ciso')}
                    style={{ padding:'13px 28px', borderRadius:9, background:'transparent', color:LAV2, border:'1px solid rgba(255,255,255,.15)', cursor:'pointer', fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:14, fontWeight:500, transition:'all .22s ease' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor='rgba(255,255,255,.35)'; e.currentTarget.style.color=LAV; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor='rgba(255,255,255,.15)'; e.currentTarget.style.color=LAV2; }}
                  >See how RevSec works</button>
                </div>
              </div>

              {/* Tags */}
              <div style={{ marginTop:48, paddingTop:32, borderTop:'1px solid rgba(255,255,255,.07)', display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
                <span style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:12, color:LAV3, marginRight:4 }}>Tags:</span>
                {['Developer Security','Vibe Coding Security','Claude Code','Cursor','GitHub Copilot','AI Governance','AI Agents','Operated AI','CISO'].map(tag => (
                  <span key={tag} style={{ padding:'4px 12px', borderRadius:100, background:'rgba(255,255,255,.05)', border:'1px solid rgba(255,255,255,.1)', fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:12, color:LAV3 }}>{tag}</span>
                ))}
              </div>

              <div style={{ marginTop:40 }}>
                <button onClick={() => navigate('/blog')}
                  style={{ background:'none', border:'none', cursor:'pointer', fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:14, color:LAV3, padding:0, display:'flex', alignItems:'center', gap:8, transition:'color .2s' }}
                  onMouseEnter={e => e.currentTarget.style.color=C}
                  onMouseLeave={e => e.currentTarget.style.color=LAV3}
                >← Back to blog</button>
              </div>
            </div>

            {/* Desktop floating share sidebar */}
            <div className="share-sidebar" style={{ paddingTop:8 }}>
              <ShareButtons/>
            </div>

          </div>
        </div>
      </div>
    </>
  );
}
